import {
	dateToS3DateString,
	DownloadRecordType,
	getFileRedisKeyFromParams,
} from '@velocity-exchange/common';
import { useState } from 'react';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import useCurrentAuthority from 'src/hooks/useCurrentAuthority';
import ExchangeHistoryClient from 'src/utils/exchangeHistoryClient';
import { VELOCITY_PROGRAM_ID, PublicKey } from '@velocity-exchange/sdk';
import { notify } from 'src/utils/notifications';
import { useSyncLocalStorage } from 'src/hooks/useSyncLocalStorage';
import { dlog } from 'src/dev';
import usePostHogCapture from 'src/hooks/posthog/usePostHogCapture';

interface Params {
	exportType: DownloadRecordType;
	from: string;
	to: string;
	userKeys: string[];
	accountOptions: { userKey: string; pubKey: PublicKey }[];
	marketSymbol?: string;
	isSubmitDisabled: boolean;
	hide: () => void;
}

export default function useExportHistoryModalSubmit({
	exportType,
	from,
	to,
	userKeys,
	accountOptions,
	marketSymbol,
	isSubmitDisabled,
	hide,
}: Params) {
	const currentAuthority = useCurrentAuthority();
	const isEmulationMode = useDriftStore((s) => s.isEmulatingAccount);
	const { captureEvent } = usePostHogCapture();

	// Comma-separated list of pending download keys so we can detect if a new one becomes available.
	const [pendingDownloadKeys, setPendingDownloadKeys] = useSyncLocalStorage(
		'pendingDownloadKeys',
		''
	);

	const [isSubmitting, setIsSubmitting] = useState(false);

	const handleSubmit = async () => {
		if (isSubmitDisabled) {
			return;
		}

		setIsSubmitting(true);

		dlog(`downloader`, `requestFileDownload`, {
			fileType: exportType,
			from,
			to,
			userPubKeys: userKeys.map(
				(userKey) =>
					accountOptions.find((acct) => acct.userKey === userKey)?.pubKey
			),
			authority: currentAuthority?.toString(),
			isEmulationMode,
			marketSymbol,
		});

		const result = await ExchangeHistoryClient.requestFileDownload({
			fileType: exportType,
			from,
			to,
			userPubKeys: userKeys
				.map(
					(userKey) =>
						accountOptions.find((acct) => acct.userKey === userKey)?.pubKey
				)
				.filter(Boolean) as PublicKey[],
			authority: currentAuthority?.toString() ?? '',
			isEmulationMode,
			marketSymbol,
		});

		captureEvent('pnl_history_exported', {
			statement_type: exportType,
			date_from: from,
			date_to: to,
		});

		if (!result.success) {
			notify({
				type: 'error',
				message: 'Error',
				description:
					'There was an error requesting the statement(s). Please try again later.',
			});

			console.error(result.message);

			setIsSubmitting(false);

			return;
		}

		const currentDate = new Date();
		const utcDate = new Date(
			currentDate.getUTCFullYear(),
			currentDate.getUTCMonth(),
			currentDate.getUTCDate()
		);

		const newKeysRequestedStr = userKeys
			.map((userKey) => {
				return getFileRedisKeyFromParams({
					fileType: exportType,
					requestedDate: dateToS3DateString(utcDate),
					fromDate: from,
					toDate: to,
					user:
						accountOptions
							.find((acct) => acct.userKey === userKey)
							?.pubKey?.toString() ?? '',
					programId: VELOCITY_PROGRAM_ID,
					authority: currentAuthority?.toString() ?? '',
					isEmulationMode,
					marketSymbol: marketSymbol ?? 'none',
				});
			})
			.concat(pendingDownloadKeys?.split(',') ?? [])
			.join(',');

		setPendingDownloadKeys(newKeysRequestedStr);

		notify({
			type: 'info',
			message: 'Generating your statement(s).',
			description:
				'This can take up to 10 minutes. You will be notified when it is ready for download.',
		});

		hide();

		setIsSubmitting(false);
	};

	return {
		isSubmitting,
		handleSubmit,
	};
}
